import axios, { type AxiosResponse, type AxiosRequestConfig } from 'axios'
import { type TypedDocumentString } from '@/gql/graphql'
import {
  ClientError,
  type GraphQLRequestContext,
  type GraphQLResponse,
} from '@/features/shared/utils/dataFetching/reactQuery/graphqlTypes'
import { isOnServer } from '@shared/constants/util'
import { isErrorMessage } from '@/features/shared/utils/guards/error'
import { getAuthClient } from '@/features/authentication/utils/authentication/client'
import { isProduction, isRunningTests } from '@/features/shared/utils/environment'
import { logDataFetchingErrors } from '@/features/shared/utils/dataFetching/utils'
import { getCommonRequestHeaders } from '@/features/shared/utils/dataFetching/requestHeaders'
import { getActiveGroupId } from '@/features/business/utils/getActiveGroupId'
import { getBaseURL } from '..'
import { getServerAccessTokenWithContext } from '@/features/shared/utils/dataFetching/accessToken'
import { type QueryContext } from '@/features/shared/utils/dataFetching/types'

const NO_TOKEN_ERROR = 'No Token for request'

// codegen string mode emits the operation first and appends fragments after it
const OPERATION_NAME = /^\s*(?:query|mutation|subscription)\s+([_A-Za-z]\w*)/

// this is cheap since the regex only looks at the first few characters of the string
export const getOperationName = (query: string) =>
  OPERATION_NAME.exec(query)?.[1] ?? 'UNKNOWN_OPERATION'

type GQLRequestOptions<TVariables> = {
  context?: QueryContext
  data: GraphQLRequestContext<TVariables>
  suppressIdHeaders?: boolean
  authOptional?: boolean
}

export enum GQL_TIMEOUTS {
  onClient = 30_000,
  onServer = 2_000,
}

export const getGqlRequestConfig = async <TVariables>({
  context,
  data,
  suppressIdHeaders = false,
  authOptional = false,
}: GQLRequestOptions<TVariables>): Promise<
  AxiosRequestConfig<GraphQLRequestContext<TVariables>>
> => {
  let token: string | undefined
  try {
    token = isOnServer()
      ? await getServerAccessTokenWithContext(context, data.query)
      : await getAuthClient().getAccessToken()
  } catch {
    // empty catch block, though we will check below if authOptional to determine if we throw or not
  }
  if (!token && !authOptional) {
    throw new Error(NO_TOKEN_ERROR)
  }

  const contextMeta = context?.meta
  const location = {
    baseURL: getBaseURL(),
    url: '/graphql-router',
  }
  const headers: AxiosRequestConfig['headers'] = {
    ...getCommonRequestHeaders({
      isGql: true,
      suppressIdHeaders,
      cookies: contextMeta?.cookies,
      headers: contextMeta?.headers,
    }),
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  }

  if (token) {
    const activeGroupId = getActiveGroupId({ cookies: contextMeta?.cookies })
    if (activeGroupId) {
      headers['X-Group-Id-Ext'] = activeGroupId
    }
  }
  return {
    ...location,
    method: 'POST',
    headers,
    data,
    params: { operationName: data.operationName },
    timeout: isOnServer() ? GQL_TIMEOUTS.onServer : GQL_TIMEOUTS.onClient,
  }
}

// We want to be cautious about including response data in error monitoring for PII reasons, specifically due to the nature
// of the BaseUser query
function stripDataFromGraphqlResponse(response: AxiosResponse<GraphQLResponse<unknown>>) {
  return {
    ...response,
    data: {
      ...response.data,
      data: {},
    },
  }
}

function getGraphqlResponseState<TData>(
  response: AxiosResponse<GraphQLResponse<TData>, unknown>,
  options?: GraphQLRequestOptions<TData>,
): 'ok' | 'fatal' | 'allowed' {
  if (!response.data.data) {
    return 'fatal'
  } else if (
    response.data.errors &&
    !options?.onError?.(response.data) &&
    // Critical GQL errors do not want this code to throw if we have a gql error
    // we only want to throw if data is null
    (options?.throwOnGqlError ?? true)
  ) {
    return 'fatal'
  } else if (response.data.errors) {
    return 'allowed'
  }
  return 'ok'
}

type GraphQLRequestOptions<TData> = {
  /*
   * onError: allow for ignoring GraphQL Error, based on response
   * true: ignore error
   * false: throw error
   * */
  onError?: (response: GraphQLResponse<TData>) => boolean
  throwOnGqlError?: boolean
  /*
   * suppressIdHeaders: suppresses the addition of x-anonymous-id and x-universal-id headers
   * This should only be used in critical api calls such as BaseUser query. When in doubt, do not provide this flag
   * Defaults to false
   * */
  suppressIdHeaders?: true
  authOptional?: boolean
  context?: QueryContext
  /** GraphQL error messages that should be logged as info instead of error */
  ignoredMessages?: (string | RegExp)[]
}
export async function fetchGraphql<TData, TVariables>(
  document: TypedDocumentString<TData, TVariables>,
  ...args: TVariables extends Record<string, never>
    ? [variables?: undefined, options?: GraphQLRequestOptions<TData>]
    : [variables: TVariables, options?: GraphQLRequestOptions<TData>]
): Promise<TData> {
  const query = String(document)
  const [variables, options] = args
  const operationName = getOperationName(query)
  // context meta is set by getQueryClient(serverContext) in SSR methods.
  const contextMeta = options?.context?.meta

  const requestBody: GraphQLRequestContext<TVariables> = {
    query,
    variables,
    operationName,
  }

  let config: AxiosRequestConfig<GraphQLRequestContext<TVariables>>

  try {
    config = await getGqlRequestConfig<TVariables>({
      context: options?.context,
      data: requestBody,
      suppressIdHeaders: !!options?.suppressIdHeaders,
      authOptional: options?.authOptional ?? false,
    })
  } catch (error) {
    if (isErrorMessage(error) && error.message === NO_TOKEN_ERROR) {
      // This will only be surfaced to engineers to help them debug potential auth issues at SSR time
      // Doing this prevents console log noise on not authed pages
      // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
      return { error: NO_TOKEN_ERROR } as unknown as TData
    }
    throw error
  }
  const requestContextHeaders = contextMeta?.headers
  try {
    const response = await axios.request<GraphQLResponse<TData>>(config)
    const responseState = getGraphqlResponseState(response, options)

    if (responseState === 'fatal' || !response.data.data) {
      throw new ClientError(stripDataFromGraphqlResponse(response), requestBody)
    }

    if (responseState === 'allowed') {
      logDataFetchingErrors({
        apiClient: {
          fetcherName: operationName,
          options: { ignoredMessages: options?.ignoredMessages },
        },
        config,
        error: new ClientError(stripDataFromGraphqlResponse(response), requestBody),
        requestContextHeaders,
        meta: {
          callLocation: 'fetchGraphql',
          graphqlError: 'allowed',
        },
      })
    }

    // first data is axios, second data is graphql
    return response.data.data
  } catch (error) {
    if (axios.isCancel(error)) {
      // logging this error locally might be useful
      // oxlint-disable-next-line no-console
      if (!isProduction && !isRunningTests) console.error(error)
    } else {
      logDataFetchingErrors({
        apiClient: {
          fetcherName: operationName,
          options: { ignoredMessages: options?.ignoredMessages },
        },
        config,
        error,
        requestContextHeaders,
        meta: {
          callLocation: 'fetchGraphql',
          graphqlError: 'fatal',
        },
      })
    }
    throw error
  }
}
